import ElementStyle from "@/constant/ElementStyle";
import MapEditType from "@/constant/MapElementType";
import BasePoint from "@/model/BasePoint";
import HeadingPoint from "@/model/HeadingPoint";
import LocLaneInfo from "@/model/LocLaneInfo";
import LocMapElement2 from "@/model/LocMapElement2";
import {
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  DoubleSide,
  EdgesGeometry,
  FrontSide,
  Group,
  Line,
  LineBasicMaterial,
  LineDashedMaterial,
  LineSegments,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshBasicMaterialParameters,
  PlaneGeometry,
  Shape,
  ShapeGeometry,
  SplineCurve,
  Texture,
  TextureLoader,
  Vector2,
} from "three";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import {
  getFuncAreaTextForType,
  getFuncPointShowForType,
  getFuncPointTextForType,
  getVerticalSlopeTwoPoint,
} from "./MyMapUtils";

const carPic = require("../assets/car_icon.png");
const funPImg1 = require("../assets/func_point_icon_1.png");
const funPImg2 = require("../assets/func_point_icon_2.png");
const blindRoad = require("../assets/blind_road_icon.png");

let carPicTexture: Texture;
let funPImg1Material: MeshBasicMaterial;
let funPImg2Material: MeshBasicMaterial;
let funcPointGeometry: PlaneGeometry;
let blindRoadMaterial: MeshBasicMaterial;

const initIconImg = () => {
  const textureLoader = new TextureLoader();
  carPicTexture = textureLoader.load(carPic);
  const funPImg1Texture = textureLoader.load(funPImg1);
  const funPImg2Texture = textureLoader.load(funPImg2);
  const funBlindRoadTexture = textureLoader.load(blindRoad);
  funcPointGeometry = new PlaneGeometry(20, 28);
  funPImg1Material = new MeshBasicMaterial({
    map: funPImg1Texture,
    side: DoubleSide,
    transparent: true,
  });
  funPImg2Material = new MeshBasicMaterial({
    map: funPImg2Texture,
    side: DoubleSide,
    transparent: true,
  });
  blindRoadMaterial = new MeshBasicMaterial({
    map: funBlindRoadTexture,
    side: DoubleSide,
    transparent: true,
  });
};

initIconImg();

const createTextCanvas = (text: string, fontSize: number, color: string) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  const textWidth = (ctx.measureText(text).width * fontSize) / 10;
  canvas.width = textWidth;
  canvas.height = fontSize;
  ctx.textBaseline = "bottom";
  ctx.fillStyle = color; //ElementStyle.point_text_color;
  ctx.font = fontSize + "px Georgia";
  ctx.lineWidth = 2;
  ctx.fillText(text, 0, fontSize);
  return canvas;
};

export const createCarIcon = (): Mesh => {
  const material = new MeshBasicMaterial({
    map: carPicTexture,
    side: DoubleSide,
    transparent: true,
  });
  const geometry = new PlaneGeometry(54, 54);
  const icon = new Mesh(geometry, material);
  return icon;
};

const createVector2s = (points: Array<BasePoint>) => {
  const vector2List = new Array<Vector2>();
  if (points?.length > 0) {
    points?.forEach((val) => {
      const p1 = new Vector2(val.x, val.y); //顶点1坐标
      vector2List.push(p1);
    });
  }
  return vector2List;
};

export const createTextMesh = (
  text: string,
  fontSize: number,
  color: string
): Mesh<PlaneGeometry, MeshBasicMaterial> => {
  const canvas = createTextCanvas(text, fontSize, color);
  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  const geometry = new PlaneGeometry(canvas.width, canvas.height);
  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
  });
  const mesh = new Mesh(geometry, material);
  return mesh;
};

export const createTowPointLine = (
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  lineMaterial: LineBasicMaterial
): Line => {
  const geometryLine = new BufferGeometry(); //声明一个几何体对象Geometry
  const pointsArray = new Array<Vector2>();
  const startP = new Vector2(startX, startY); //顶点1坐标
  const endP = new Vector2(endX, endY); //顶点1坐标
  pointsArray.push(startP);
  pointsArray.push(endP);
  //顶点坐标添加到geometry对象
  geometryLine.setFromPoints(pointsArray);
  //线条模型对象
  const line = new Line(geometryLine, lineMaterial);
  line.computeLineDistances();
  return line;
};

export const createSimpleLine = (
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  color: string,
  linewidth: number
): Line => {
  const lineMaterial = new LineBasicMaterial({
    color: color,
    linewidth: linewidth,
  });
  //线条模型对象
  const line = createTowPointLine(startX, startY, endX, endY, lineMaterial);
  return line;
};

export const createSimpleDashLine = (
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  color: string,
  linewidth: number,
  dashSize: number,
  gapSize: number
): Line => {
  const lineMaterial = new LineDashedMaterial({
    color: color,
    dashSize: dashSize, //短划线的大小
    gapSize: gapSize, //短划线之间的距离
    linewidth: linewidth,
  });
  //线条模型对象
  const line = createTowPointLine(startX, startY, endX, endY, lineMaterial);
  return line;
};

const createLineVector2s = (vector2s: Vector2[], material: Material) => {
  if (vector2s?.length > 0) {
    const geometryLine = new BufferGeometry(); //声明一个几何体对象Geometry
    //顶点坐标添加到geometry对象
    geometryLine.setFromPoints(vector2s);
    //线条模型对象
    const line = new Line(geometryLine, material);
    line.computeLineDistances();
    return line;
  } else {
    return null;
  }
};

const createWidthLine = (linePoints: BasePoint[], material: LineMaterial) => {
  if (linePoints?.length > 0) {
    const geometryLine = new LineGeometry(); //声明一个几何体对象Geometry
    let points = new Array<number>();
    for (let i = 0; i < linePoints?.length; i++) {
      if (!isNaN(linePoints[i].x) && !isNaN(linePoints[i].y)) {
        points.push(linePoints[i].x);
        points.push(linePoints[i].y);
        points.push(0);
      }
    }
    //顶点坐标添加到geometry对象
    if (points.length > 0) {
      geometryLine.setPositions(points);
      points = [];
      // 4. 设置材质分辨率
      material.resolution.set(window.innerWidth, window.innerHeight);
      //线条模型对象
      const line = new Line2(geometryLine, material);
      line.computeLineDistances();
      return line;
    } else {
      return null;
    }
  } else {
    return null;
  }
};

const createLine = (linePoints: BasePoint[], material: Material) => {
  if (linePoints?.length > 0) {
    const geometryLine = new BufferGeometry(); //声明一个几何体对象Geometry
    let pointsArray = new Array<Vector2>();
    for (let i = 0; i < linePoints?.length; i++) {
      const p1 = new Vector2(linePoints[i].x, linePoints[i].y); //顶点1坐标
      pointsArray.push(p1);
      i++;
    }
    //顶点坐标添加到geometry对象
    geometryLine.setFromPoints(pointsArray);
    pointsArray = [];
    //线条模型对象
    const line = new Line(geometryLine, material);
    line.computeLineDistances();
    return line;
  } else {
    return null;
  }
};

export const createGriddingScaleLine = (
  scalePix: number,
  width: number,
  height: number
): Group | null => {
  if (scalePix > 0) {
    const lineGroup = new Group();
    const widthNum = width / scalePix;
    const heightNum = height / scalePix;
    const tx = width / 2;
    const ty = height / 2;
    const lineMaterial = new LineBasicMaterial({
      color: ElementStyle.gridding_line_color,
      linewidth: ElementStyle.gridding_line_width,
    });
    const widthLeft = new Array<Vector2>();
    const widthRight = new Array<Vector2>();
    const heightLeft = new Array<Vector2>();
    const heightRight = new Array<Vector2>();
    let lastTy = ty + 2;
    for (let i = 0; i <= widthNum / 2 + 1; i++) {
      const x = i * scalePix;
      const p1 = new Vector2(x, lastTy); //顶点1坐标
      const p2 = new Vector2(x, -lastTy); //顶点1坐标
      widthLeft.push(p1);
      widthLeft.push(p2);
      const pr1 = new Vector2(-x, lastTy); //顶点1坐标
      const pr2 = new Vector2(-x, -lastTy); //顶点1坐标
      lastTy = -lastTy;
      widthRight.push(pr1);
      widthRight.push(pr2);
    }
    let lastTx = tx + 2;
    for (let i = 0; i <= heightNum / 2 + 1; i++) {
      const y = i * scalePix;
      const p1 = new Vector2(lastTx, y); //顶点1坐标
      const p2 = new Vector2(-lastTx, y); //顶点1坐标
      heightLeft.push(p1);
      heightLeft.push(p2);
      const pr1 = new Vector2(lastTx, -y); //顶点1坐标
      const pr2 = new Vector2(-lastTx, -y); //顶点1坐标
      heightRight.push(pr1);
      heightRight.push(pr2);
      lastTx = -lastTx;
    }
    const line1 = createLineVector2s(widthLeft, lineMaterial) as Line;
    const line2 = createLineVector2s(widthRight, lineMaterial) as Line;
    const line3 = createLineVector2s(heightLeft, lineMaterial) as Line;
    const line4 = createLineVector2s(heightRight, lineMaterial) as Line;
    lineGroup.add(line1);
    lineGroup.add(line2);
    lineGroup.add(line3);
    lineGroup.add(line4);
    return lineGroup;
  } else {
    return null;
  }
};

const createShapeGeometry = (linePoints: LocMapElement2) => {
  if (linePoints?.points?.length > 0) {
    const pointsArray = new Array<Vector2>();
    for (let i = 0; i < linePoints?.points?.length; i++) {
      const p1 = new Vector2(linePoints?.points[i].x, linePoints?.points[i].y); //顶点1坐标
      pointsArray.push(p1);
    }
    //顶点坐标添加到geometry对象
    const shape = new Shape(pointsArray);
    const geometry = new ShapeGeometry(shape); //声明一个几何体对象Geometry
    return geometry;
  } else {
    return null;
  }
};

const createSplineCurveLine = (
  linePoints: HeadingPoint[],
  material: Material
) => {
  if (linePoints?.length > 0) {
    const geometryLine = new BufferGeometry(); //声明一个几何体对象Geometry
    let pointsArray = new Array<Vector2>();
    for (let i = 0; i < linePoints?.length; i++) {
      const p1 = new Vector2(linePoints[i].x, linePoints[i].y); //顶点1坐标
      pointsArray.push(p1);
    }
    const curve = new SplineCurve(pointsArray);
    //顶点坐标添加到geometry对象
    geometryLine.setFromPoints(curve.getPoints(linePoints?.length * 20));
    pointsArray = [];
    //线条模型对象
    const line = new Line(geometryLine, material);
    line.computeLineDistances();

    return line;
  } else {
    return null;
  }
};

const createFloorLampsF = (linePoints: LocMapElement2) => {
  if (linePoints?.points?.length > 0) {
    const blindRoadGeometry = new CircleGeometry(8, 20);
    const iconMesh = new Mesh(blindRoadGeometry, blindRoadMaterial);
    iconMesh.position.setX(linePoints.points[0].x);
    iconMesh.position.setY(linePoints.points[0].y);
    return iconMesh;
  }
};

const createAreaLineFun = (mapElement: LocMapElement2, scale = 1) => {
  const width = mapElement.extension?.blind_road_width as number;
  const material = new LineBasicMaterial({
    color: ElementStyle.blind_road_color_default,
    linewidth: width * 20 * scale,
  });
  const checkedMaterial = new LineBasicMaterial({
    color: ElementStyle.wall_color_checked,
    linewidth: width * 20 * scale,
  });
  let curMaterial: LineBasicMaterial;
  if (mapElement.selectState && checkedMaterial) {
    curMaterial = checkedMaterial;
  } else {
    curMaterial = material;
  }
  const line = createSplineCurveLine(mapElement.points, curMaterial);
  return line;
};

const createTransparentLine = (linePoints: HeadingPoint[], width: number) => {
  if (linePoints?.length > 0) {
    const geometryLine = new LineGeometry(); //声明一个几何体对象Geometry
    let points = new Array<number>();
    for (let i = 0; i < linePoints?.length; i++) {
      if (!isNaN(linePoints[i].x) && !isNaN(linePoints[i].y)) {
        points.push(linePoints[i].x);
        points.push(linePoints[i].y);
        points.push(0);
      }
    }
    //顶点坐标添加到geometry对象
    if (points.length > 0) {
      geometryLine.setPositions(points);
      points = [];
      // 4. 设置材质分辨率
      const curMaterial = new LineMaterial({
        color: ElementStyle.wall_color_checked_number,
        transparent: true,
        opacity: 0,
      });
      curMaterial.linewidth = width;
      curMaterial.resolution.set(window.innerWidth, window.innerHeight);
      //线条模型对象
      const line = new Line2(geometryLine, curMaterial);
      line.computeLineDistances();
      return line;
    } else {
      return null;
    }
  } else {
    return null;
  }
};

const createAreaFun = (
  linePoints: LocMapElement2,
  material: MeshBasicMaterial,
  edgesMtl: LineBasicMaterial
) => {
  const shape = createShapeGeometry(linePoints);
  if (shape) {
    const mesh = new Mesh(shape, material);
    if (linePoints.selectState) {
      const cubeEdges = new EdgesGeometry(shape, 1);
      const cubeLine = new LineSegments(cubeEdges, edgesMtl);
      mesh.add(cubeLine);
    }
    return mesh;
  }
  return null;
};

export const createFuncAreas = (
  mapElements: LocMapElement2[]
): Array<Mesh | Line | Line2> => {
  const material = new MeshBasicMaterial({
    color: ElementStyle.func_area_color_default_bg,
    opacity: 0.3, //透明度
    transparent: true,
  });
  const edgesMtl = new LineBasicMaterial({
    color: ElementStyle.func_area_color_checked_line,
  });
  const meshList = new Array<Mesh | Line | Line2>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2) => {
      if (
        linePoints.sub_type == MapEditType.edit_func_area_type_floor_lamps_9
      ) {
        const mesh = createFloorLampsF(linePoints);
        if (mesh) {
          meshList.push(mesh);
        }
      } else if (
        linePoints.sub_type == MapEditType.edit_func_area_type_blindPathArea_10
      ) {
        const line = createAreaLineFun(linePoints);
        const line2 = createTransparentLine(
          linePoints.points,
          ElementStyle.transparent_line_width
        );
        if (line) {
          meshList.push(line);
        }
        if (line2) {
          meshList.push(line2);
        }
      } else {
        const mesh = createAreaFun(linePoints, material, edgesMtl);
        if (mesh) {
          meshList.push(mesh);
        }
      }
    });
  }
  return meshList;
};

const createFuncPoint = (
  pointX: number,
  pointY: number,
  type: number,
  name: string,
  material: Material,
  geometry: BufferGeometry
) => {
  const pointImgHeight = 28;
  const iconGroup = new Group();
  iconGroup.position.setX(pointX);
  iconGroup.position.setY(pointY);
  const iconMesh = new Mesh(geometry, material);
  iconMesh.position.setY(pointImgHeight / 2);
  iconGroup.add(iconMesh);
  let textY = pointImgHeight / 2;
  const typeText = getFuncPointTextForType(type);
  if (typeText) {
    textY += -(
      ElementStyle.point_text_font_size +
      ElementStyle.point_text_font_size / 2
    );
    const pointTypeMesh = createTextMesh(
      typeText,
      ElementStyle.point_text_font_size,
      ElementStyle.point_text_color
    );
    pointTypeMesh.position.setY(textY);
    iconGroup.add(pointTypeMesh);
  }
  if (name) {
    const textMesh = createTextMesh(
      name,
      ElementStyle.point_text_font_size,
      ElementStyle.point_text_color
    );
    textY += -(
      ElementStyle.point_text_font_size +
      ElementStyle.point_text_font_size / 3
    );
    textMesh.position.setY(textY);
    iconGroup.add(textMesh);
  }

  return iconGroup;
};

const createCheckedFuncPoint = (
  pointX: number,
  pointY: number,
  type: number,
  name: string
) => {
  const iconGroup = createFuncPoint(
    pointX,
    pointY,
    type,
    name,
    funPImg2Material,
    funcPointGeometry
  );
  return iconGroup;
};

export const createDefaultFuncPoint = (
  pointX: number,
  pointY: number,
  type: number,
  name: string
): Group => {
  const iconGroup = createFuncPoint(
    pointX,
    pointY,
    type,
    name,
    funPImg1Material,
    funcPointGeometry
  );
  return iconGroup;
};

const createFuncPointsGroupList = (
  mapElements: LocMapElement2[],
  checkedId = "",
  showOnlyChecked = false
): Group[] => {
  const meshList = new Array<Group>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2) => {
      const show = getFuncPointShowForType(linePoints.sub_type);
      if (show && linePoints?.points?.length > 0) {
        const linePoint = linePoints?.points[0];
        if (linePoint) {
          let iconGroup: Group | null;
          iconGroup = null;
          if (checkedId == linePoints.id || linePoints.selectState) {
            iconGroup = createCheckedFuncPoint(
              linePoint.x,
              linePoint.y,
              linePoints.sub_type,
              linePoints.name
            );
          } else if (!showOnlyChecked) {
            iconGroup = createDefaultFuncPoint(
              linePoint.x,
              linePoint.y,
              linePoints.sub_type,
              linePoints.name
            );
          }
          if (iconGroup) {
            meshList.push(iconGroup);
          }
        }
      }
    });
  }
  return meshList;
};

export const createFuncPoints = (
  mapElements: LocMapElement2[],
  checkedId = "",
  showOnlyChecked = false
): Group[] => {
  return createFuncPointsGroupList(mapElements, checkedId, showOnlyChecked);
};

const getFuncAreaBottomPoint = (funcArea: LocMapElement2) => {
  let temPoint: BasePoint | undefined;
  funcArea?.points?.forEach((point: BasePoint) => {
    if (temPoint) {
      if (temPoint.y > point.y) {
        temPoint = point;
      }
    } else {
      temPoint = point;
    }
  });
  return temPoint;
};

export const createFuncAreasTextGroups = (
  mapElements: LocMapElement2[]
): Group[] => {
  const meshList = new Array<Group>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2) => {
      if (linePoints?.points?.length > 0) {
        const bottomP = getFuncAreaBottomPoint(linePoints);
        if (bottomP) {
          const iconGroup = new Group();
          iconGroup.position.setX(bottomP.x);
          iconGroup.position.setY(bottomP.y);
          let textY = 0;
          const typeText = getFuncAreaTextForType(linePoints.sub_type);
          if (typeText) {
            textY = -ElementStyle.point_text_font_size;
            const pointTypeMesh = createTextMesh(
              typeText,
              ElementStyle.point_text_font_size,
              ElementStyle.point_text_color
            );
            pointTypeMesh.position.setY(textY);
            iconGroup.add(pointTypeMesh);
          }
          if (linePoints.name) {
            const textMesh = createTextMesh(
              linePoints.name,
              ElementStyle.point_text_font_size,
              ElementStyle.point_text_color
            );
            textY += -(
              ElementStyle.point_text_font_size +
              ElementStyle.point_text_font_size / 3
            );
            textMesh.position.setY(textY);
            iconGroup.add(textMesh);
            meshList.push(iconGroup);
          }
        }
      }
    });
  }
  return meshList;
};

const createWidthLines = (
  mapElements: LocMapElement2[],
  material: LineMaterial,
  checkedMaterial?: LineMaterial
): Line2[] => {
  const lineList = new Array<Line2>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2) => {
      let curMaterial: LineMaterial;
      if (linePoints.selectState && checkedMaterial) {
        curMaterial = checkedMaterial;
      } else {
        curMaterial = material;
      }
      const line = createWidthLine(linePoints.points, curMaterial);
      if (line) {
        lineList.push(line);
      }
    });
  }
  return lineList;
};

const createLines = (
  mapElements: LocMapElement2[],
  material: Material,
  checkedMaterial?: Material
): Line[] => {
  const lineList = new Array<Line>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2) => {
      let curMaterial: Material;
      if (linePoints.selectState && checkedMaterial) {
        curMaterial = checkedMaterial;
      } else {
        curMaterial = material;
      }
      const line = createLine(linePoints.points, curMaterial);
      if (line) {
        lineList.push(line);
      }
    });
  }
  return lineList;
};

export const createAccessLineArea = (
  p1: HeadingPoint,
  p2: HeadingPoint,
  width: number,
  parameters?: MeshBasicMaterialParameters
): Mesh | null => {
  //创建门禁线区域
  if (p1 && p2 && width > 0) {
    const aline = getVerticalSlopeTwoPoint(
      p1.x,
      p1.y,
      p2.x,
      p2.y,
      width / 2,
      p1.x,
      p1.y
    ) as HeadingPoint[];
    const bline = getVerticalSlopeTwoPoint(
      p1.x,
      p1.y,
      p2.x,
      p2.y,
      width / 2,
      p2.x,
      p2.y
    ) as HeadingPoint[];
    const element = {} as LocMapElement2;
    element.points = [];
    element.points.push(aline[0], bline[0], bline[1], aline[1]);
    const material = new MeshBasicMaterial(parameters);

    const mashArea = createShapeMesh(element, material) as Mesh;
    mashArea.userData = { name: "AccessLineArea" };
    return mashArea;
  } else {
    return null;
  }
};

const createAccessLinePoints = (
  p1: HeadingPoint,
  p2: HeadingPoint,
  pointDis: number,
  iconMaterial?: Material
) => {
  if (
    typeof p1?.x === "number" &&
    typeof p1?.y === "number" &&
    typeof p2?.x === "number" &&
    typeof p2?.y === "number" &&
    pointDis > 0
  ) {
    const centerPint = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
    const accessPoints = getVerticalSlopeTwoPoint(
      p1.x,
      p1.y,
      p2.x,
      p2.y,
      pointDis,
      centerPint.x,
      centerPint.y
    ) as HeadingPoint[];

    const pointImgHeight = 28;
    const iconMesh1 = new Mesh(funcPointGeometry, iconMaterial);
    iconMesh1.position.setY(pointImgHeight / 2);
    iconMesh1.position.setX(accessPoints[0].x);
    iconMesh1.position.setY(accessPoints[0].y);

    const iconMesh2 = new Mesh(funcPointGeometry, iconMaterial);
    iconMesh2.position.setY(pointImgHeight / 2);
    iconMesh2.position.setX(accessPoints[1].x);
    iconMesh2.position.setY(accessPoints[1].y);
    return [iconMesh1, iconMesh2];
  } else {
    return null;
  }
};

export const createVirtualWalls = (mapElements: LocMapElement2[]): Line2[] => {
  const lineMaterial = new LineMaterial({
    color: ElementStyle.wall_color_default,
    linewidth: ElementStyle.virtual_wall_line_width,
  });
  const lineCheckedMaterial = new LineMaterial({
    color: ElementStyle.wall_color_checked,
    linewidth: ElementStyle.virtual_wall_line_width,
  });
  return createWidthLines(mapElements, lineMaterial, lineCheckedMaterial);
};

export const createAccessLines = (mapElements: LocMapElement2[]): Group[] => {
  let lineArea: Mesh | null;
  const lineGroups = new Array<Group>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2) => {
      const group = new Group();
      let iconMaterial: Material;
      if (linePoints.selectState) {
        iconMaterial = funPImg2Material;
      } else {
        iconMaterial = funPImg1Material;
      }
      const lcEle = JSON.parse(JSON.stringify(linePoints)) as LocMapElement2;
      const lineP = new Array<HeadingPoint>();
      lcEle.points = lineP;
      if (linePoints?.points?.length > 0) {
        lcEle.points.push(linePoints.points[0]);
      }
      if (linePoints?.points?.length > 1) {
        lcEle.points.push(linePoints.points[1]);
        lineArea = createAccessLineArea(
          linePoints.points[0],
          linePoints.points[1],
          lcEle?.extension?.doorWidth as number,
          {
            color: ElementStyle.func_area_color_default_bg,
            opacity: 0.3, //透明度
            transparent: true,
          }
        );
      }
      let color: number;
      if (linePoints.selectState) {
        color = ElementStyle.wall_color_checked;
      } else {
        color = ElementStyle.wall_color_default;
      }
      const line = createAccessLineArea(
        linePoints.points[0],
        linePoints.points[1],
        ElementStyle.access_line_width,
        {
          color: color,
        }
      );
      if (linePoints.points?.length > 1) {
        const points = createAccessLinePoints(
          linePoints.points[0],
          linePoints.points[1],
          lcEle?.extension?.doorPointDis as number,
          iconMaterial
        );
        if (points && points?.length > 0) {
          points.forEach((val) => {
            group.add(val);
          });
        }
      }
      if (linePoints.points?.length > 3) {
        const iconMesh = new Mesh(funcPointGeometry, iconMaterial);
        const pointImgHeight = 28;
        iconMesh.position.setY(pointImgHeight / 2);
        iconMesh.position.setX(linePoints.points[3].x);
        iconMesh.position.setY(linePoints.points[3].y);
        group.add(iconMesh);
      }
      if (line) {
        if (lineArea) {
          group.add(lineArea);
        }
        group.add(line);
        lineGroups.push(group);
      }
    });
  }
  return lineGroups;
};

const createShapeMesh = (linePoints: LocMapElement2, material: Material) => {
  if (linePoints?.points?.length > 0) {
    const geometry = createShapeGeometry(linePoints) as BufferGeometry;
    const mesh = new Mesh(geometry, material);
    return mesh;
  } else {
    return null;
  }
};

export const createNoiseAreas = (mapElements: LocMapElement2[]): Mesh[] => {
  const material1 = new MeshBasicMaterial({
    color: ElementStyle.noise_area_color_default_bg_1,
  });
  const material2 = new MeshBasicMaterial({
    color: ElementStyle.noise_area_color_default_bg_2,
  });
  const meshList = new Array<Mesh>();
  if (mapElements?.length > 0) {
    mapElements.forEach((linePoints: LocMapElement2, index: number) => {
      let material: MeshBasicMaterial;
      if (linePoints.sub_type == MapEditType.noisy_type_clean_1) {
        material = material1;
      } else {
        material = material2;
      }
      const mesh = createShapeMesh(linePoints, material);
      if (mesh) {
        mesh.renderOrder = 0.1 + index / 100;
        meshList.push(mesh);
      }
    });
  }
  return meshList;
};

export const createLaneInfoMeshImg = (
  laneInfo: LocLaneInfo,
  loadBack: (value: Mesh) => void
): void => {
  const picStr = laneInfo.lane_url;
  const textureLoader = new TextureLoader();
  textureLoader.load(
    picStr,
    (texture: any) => {
      const material = new MeshBasicMaterial({
        map: texture,
        side: FrontSide,
        transparent: true,
      });
      const picWidth = texture.image.width as number;
      const picHeight = texture.image.height as number;
      const geometry = new PlaneGeometry(picWidth, picHeight);
      console.log("任务路径图片加载成功 ");
      const picMesh = new Mesh(geometry, material);
      picMesh.position.setX(laneInfo.left_up_coordinate.x + picWidth / 2);
      picMesh.position.setY(laneInfo.left_up_coordinate.y + picHeight / 2);
      if (loadBack) {
        loadBack(picMesh);
      }
    },
    (event: ProgressEvent) => {
      console.log("任务路径图片加载进度 ", event);
    },
    (event: any) => {
      console.log("任务路径图片加载错误 ", event);
    }
  );
};
